// Applications.js — redesigned page (B2B SaaS layout)
import React, { useState, useEffect, useMemo } from 'react';
import Sidebar from './sidebar';
import ApplicationsModalConfirm from './modal_windows/ApplicationsModalConfirm';
import ApplicationsModalCancellation from './modal_windows/ApplicationsModalCancellation';
import ApplicationsDetailsModal from './modal_windows/ApplicationsDetailsModal';

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';

import {
  StatCard, RefreshButton, FilterPill, SearchField,
  StatusBadge, TableCard, LoadingState, EmptyState,
  Pagination, BulkBar,
} from '../../ui/CrmPage';

const getWeekNumber = (dateString) => {
  const date = new Date(dateString);
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() + 4 - (date.getDay() || 7));
  const yearStart = new Date(date.getFullYear(), 0, 1);
  return Math.ceil((((date - yearStart) / 86400000) + 1) / 7);
};

const formatDate = (s) => {
  if (!s) return '—';
  const d = new Date(s);
  return `${d.getUTCDate().toString().padStart(2, '0')}.${(d.getUTCMonth() + 1).toString().padStart(2, '0')}.${d.getUTCFullYear()}`;
};

const KZT = new Intl.NumberFormat('ru-KZ', { maximumFractionDigits: 0 });

const Applications = () => {
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showCancellationModal, setShowCancellationModal] = useState(false);
  const [selectedApplications, setSelectedApplications] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);

  const [searchTerm, setSearchTerm] = useState('');
  const [regions, setRegions] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState('Все');

  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const ordersPerPage = 15;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetch(`${process.env.REACT_APP_API_URL}v1/tickets_view`)
      .then(res => res.ok ? res.json() : Promise.reject('Ошибка сети'))
      .then(async data => {
        const detailedApplications = await Promise.all(
          data.map(async ticket => {
            const details = await fetch(
              `${process.env.REACT_APP_API_URL}v1/tickets/${ticket.id_orders}`
            ).then(r => r.json()).catch(() => ({}));
            return { ...ticket, ...details };
          })
        );

        const grouped = detailedApplications.reduce((acc, ticket) => {
          if (!acc[ticket.id_orders]) {
            acc[ticket.id_orders] = {
              id_orders: ticket.id_orders,
              order_number: ticket.order_number,
              status_name: ticket.status_name,
              client_name: ticket.client_name,
              delivery_address: ticket.delivery_address,
              order_date: ticket.order_date,
              contract_info: ticket.contract_info,
              delivery_date: ticket.delivery_date,
              items: [],
              id_organization: ticket.id_organization || '',
              total_amount: ticket.total_amount || 0,
              payment_status: ticket.id_payment_status === 1 ? 'Оплачено' : 'Ожидает оплаты',
              id_status: ticket.id_status || '',
              week_number: ticket.week_number || null,
              id_payment_status: ticket.id_payment_status || 2,
              region_name: ticket.region_name || 'Не указан',
            };
          }
          acc[ticket.id_orders].items.push(ticket);
          return acc;
        }, {});

        if (cancelled) return;
        const apps = Object.values(grouped);
        setApplications(apps);
        setRegions([...new Set(apps.map(a => a.region_name))]);
      })
      .catch(console.error)
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [reloadKey]);

  const summary = useMemo(() => {
    const total = applications.length;
    const pending = applications.filter(a => /ожидает|новая|подтверждения/i.test(a.status_name || '')).length;
    const confirmed = applications.filter(a => /подтвержд/i.test(a.status_name || '') || a.id_status === 6).length;
    const totalAmount = applications.reduce((sum, a) => sum + (Number(a.total_amount) || 0), 0);
    return { total, pending, confirmed, totalAmount };
  }, [applications]);

  const filteredApplications = useMemo(() => {
    const term = (searchTerm || '').toLowerCase().trim();
    return applications.filter(app => {
      if (selectedRegion !== 'Все' && app.region_name !== selectedRegion) return false;
      if (!term) return true;
      return (
        (app.client_name || '').toLowerCase().includes(term) ||
        (app.delivery_address || '').toLowerCase().includes(term) ||
        String(app.order_number || '').includes(term) ||
        (app.contract_info || '').toLowerCase().includes(term)
      );
    });
  }, [applications, searchTerm, selectedRegion]);

  const indexOfLast = currentPage * ordersPerPage;
  const indexOfFirst = indexOfLast - ordersPerPage;
  const currentOrders = filteredApplications.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(filteredApplications.length / ordersPerPage);

  const handleCheckboxChange = (event, index) => {
    event.stopPropagation();
    setSelectedApplications(prev =>
      prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]
    );
  };

  const handleConfirm = (weekNumber) => {
    const selectedData = selectedApplications.map(i => applications[i]);
    Promise.all(
      selectedData.map(app =>
        fetch(`${process.env.REACT_APP_API_URL}v1/tickets/${app.id_orders}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id_organization: app.id_organization,
            total_amount: app.total_amount,
            payment_status: app.id_payment_status,
            id_status: 6,
            id_division: null,
            id_drivers: null,
            delivery_date: app.delivery_date,
            week_number: weekNumber,
            delivery_address: app.delivery_address,
          }),
        })
      )
    ).then(() => {
      setApplications(prev => prev.filter(app => !selectedApplications.includes(applications.indexOf(app))));
      setSelectedApplications([]);
      setShowConfirmModal(false);
    });
  };

  const handleCancel = () => {
    const selectedData = selectedApplications.map(i => applications[i]);
    Promise.all(
      selectedData.map(app =>
        fetch(`${process.env.REACT_APP_API_URL}v1/tickets/${app.id_orders}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id_organization: app.id_organization,
            total_amount: app.total_amount,
            payment_status: app.id_payment_status,
            id_status: 10,
            delivery_date: app.delivery_date,
            week_number: app.week_number,
            delivery_address: app.delivery_address,
          }),
        })
      )
    ).then(() => {
      setApplications(prev => prev.filter(app => !selectedApplications.includes(applications.indexOf(app))));
      setSelectedApplications([]);
      setShowCancellationModal(false);
    });
  };

  const handleRowClick = (app) => {
    setSelectedOrder(app);
    setIsDetailsOpen(true);
  };

  const toggleAll = () => {
    if (selectedApplications.length === currentOrders.length) {
      setSelectedApplications([]);
    } else {
      const all = currentOrders.map(app => applications.indexOf(app));
      setSelectedApplications(all);
    }
  };

  const resetFilters = () => {
    setSearchTerm('');
    setSelectedRegion('Все');
    setCurrentPage(1);
  };

  const hasFilters = searchTerm || selectedRegion !== 'Все';

  return (
    <div className="applications-basic-container crmpage">
      <div className="applications-sidebar-container">
        <Sidebar />
      </div>

      <div className="applications-basic-content crmpage__main">
        <header className="crmpage__head">
          <h1 className="crmpage__title">Заявки</h1>
        </header>

        <section className="crmpage__stats">
          <StatCard label="Всего"          value={summary.total}     accent="neutral" />
          <StatCard label="Ожидают"        value={summary.pending}   accent="warning" />
          <StatCard label="Подтверждены"   value={summary.confirmed} accent="success" />
          <StatCard label="Сумма заявок"   value={`${KZT.format(summary.totalAmount)} ₸`} accent="brand" wide />
          <RefreshButton onClick={() => setReloadKey(k => k + 1)} />
        </section>

        <section className="crmpage__filters">
          <SearchField
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            placeholder="Поиск: номер, клиент, адрес, договор…"
          />
          <div className="crmpage__pills">
            <FilterPill
              active={selectedRegion === 'Все'}
              onClick={() => { setSelectedRegion('Все'); setCurrentPage(1); }}
              count={applications.length}
            >Все регионы</FilterPill>
            {regions.map(r => (
              <FilterPill
                key={r}
                active={selectedRegion === r}
                onClick={() => { setSelectedRegion(r); setCurrentPage(1); }}
                count={applications.filter(a => a.region_name === r).length}
              >{r}</FilterPill>
            ))}
          </div>
          {hasFilters && (
            <button type="button" className="crmpage__reset" onClick={resetFilters}>Сбросить</button>
          )}
        </section>

        <TableCard>
          {loading ? (
            <LoadingState text="Загружаем заявки…" />
          ) : currentOrders.length === 0 ? (
            <EmptyState
              title="Заявок не найдено"
              text={hasFilters
                ? 'Попробуйте сбросить фильтры или изменить запрос.'
                : 'Новые заявки появятся здесь автоматически.'}
            />
          ) : (
            <>
              <div className="crmpage__table-wrap">
                <table className="crmpage__table">
                  <colgroup>
                    <col style={{ width: 40 }} />
                    <col style={{ width: 80 }} />
                    <col />
                    <col />
                    <col />
                    <col style={{ width: 160 }} />
                    <col style={{ width: 100 }} />
                    <col style={{ width: 100 }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th className="crmpage__th-check">
                        <input
                          type="checkbox"
                          className="crmpage__check"
                          checked={currentOrders.length > 0 && selectedApplications.length === currentOrders.length}
                          onChange={toggleAll}
                          aria-label="Выделить все"
                        />
                      </th>
                      <th>Номер</th>
                      <th>Клиент</th>
                      <th className="crmpage__hide-880">Адрес доставки</th>
                      <th className="crmpage__hide-1180">Договор</th>
                      <th>Статус</th>
                      <th className="crmpage__hide-1000">Дата заказа</th>
                      <th>Дата доставки</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentOrders.map((app, idx) => {
                      const realIndex = applications.indexOf(app);
                      const checked = selectedApplications.includes(realIndex);
                      return (
                        <tr
                          key={app.id_orders ?? idx}
                          className={checked ? 'crmpage__row crmpage__row--selected' : 'crmpage__row'}
                          onClick={() => handleRowClick(app)}
                        >
                          <td className="crmpage__td-check" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              className="crmpage__check"
                              checked={checked}
                              onChange={(e) => handleCheckboxChange(e, realIndex)}
                            />
                          </td>
                          <td><span className="crmpage__num">№{app.order_number}</span></td>
                          <td>
                            <div className="crmpage__primary">{app.client_name || '—'}</div>
                            <div className="crmpage__sub">{app.region_name}</div>
                          </td>
                          <td className="crmpage__muted crmpage__hide-880">{app.delivery_address || '—'}</td>
                          <td className="crmpage__muted crmpage__hide-1180">{app.contract_info || '—'}</td>
                          <td><StatusBadge name={app.status_name} /></td>
                          <td className="crmpage__date crmpage__hide-1000">{formatDate(app.order_date)}</td>
                          <td className="crmpage__date">{formatDate(app.delivery_date)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile card-list */}
              <div className="crmpage__cards">
                {currentOrders.map((app, idx) => {
                  const realIndex = applications.indexOf(app);
                  const checked = selectedApplications.includes(realIndex);
                  return (
                    <div
                      key={app.id_orders ?? idx}
                      className={`crmpage__card ${checked ? 'is-selected' : ''}`}
                      onClick={() => handleRowClick(app)}
                    >
                      <div className="crmpage__card-head">
                        <label className="crmpage__card-check" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            className="crmpage__check"
                            checked={checked}
                            onChange={(e) => handleCheckboxChange(e, realIndex)}
                          />
                          <span className="crmpage__num">№{app.order_number}</span>
                        </label>
                        <StatusBadge name={app.status_name} />
                      </div>
                      <div className="crmpage__card-row">
                        <span className="crmpage__card-label">Клиент</span>
                        <span className="crmpage__card-val">{app.client_name || '—'}</span>
                      </div>
                      <div className="crmpage__card-row">
                        <span className="crmpage__card-label">Адрес</span>
                        <span className="crmpage__card-val">{app.delivery_address || '—'}</span>
                      </div>
                      <div className="crmpage__card-row">
                        <span className="crmpage__card-label">Доставка</span>
                        <span className="crmpage__card-val">{formatDate(app.delivery_date)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>

              <Pagination current={currentPage} total={totalPages} onChange={setCurrentPage} />
            </>
          )}
        </TableCard>

        <BulkBar
          count={selectedApplications.length}
          onClear={() => setSelectedApplications([])}
        >
          <button
            type="button"
            className="crmpage__btn crmpage__btn--ghost"
            onClick={() => setShowCancellationModal(true)}
          >
            <CancelOutlinedIcon sx={{ fontSize: 18 }} />
            Отменить
          </button>
          <button
            type="button"
            className="crmpage__btn crmpage__btn--primary"
            onClick={() => setShowConfirmModal(true)}
          >
            <CheckCircleOutlineIcon sx={{ fontSize: 18 }} />
            Подтвердить
          </button>
        </BulkBar>

        {isDetailsOpen && selectedOrder && (
          <ApplicationsDetailsModal
            isOpen={isDetailsOpen}
            onClose={() => setIsDetailsOpen(false)}
            application={selectedOrder}
          />
        )}
        {showConfirmModal && (
          <ApplicationsModalConfirm
            onClose={() => setShowConfirmModal(false)}
            onConfirm={handleConfirm}
            getWeekNumber={getWeekNumber}
          />
        )}
        {showCancellationModal && (
          <ApplicationsModalCancellation
            onClose={() => setShowCancellationModal(false)}
            onConfirm={handleCancel}
          />
        )}
      </div>
    </div>
  );
};

export default Applications;
